import {useEffect, useRef} from "react";
import {prerendering} from "./prerendering";

// Cloudflare Turnstile, the bot check on the sign-in, forgotten password, new support ticket and Contact us forms: Cloudflare's script, loaded the first time the
// widget is shown (serverless.yml's Content-Security-Policy allows challenges.cloudflare.com). onToken gets the token to
// send with the form (the API checks it with Cloudflare), or undefined when it expires; onError says the script couldn't
// load or the check didn't pass. A token works once, so forms show a new widget (a new key) after each try: see
// useTurnstile, which forms use rather than this. Without a site key (TURNSTILE_SITE_KEY), nothing is shown.
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

interface TurnstileApi {
    render(element: HTMLElement, options: {
        sitekey: string,
        callback(token: string): void,
        'expired-callback'(): void,
        'error-callback'(): void,
        theme?: 'light' | 'dark' | 'auto'
    }): string;
    remove(widgetId: string): void;
}

let loading: Promise<TurnstileApi> | undefined;

function loadTurnstile(): Promise<TurnstileApi> {
    loading ??= new Promise<TurnstileApi>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = SCRIPT_URL;
        script.async = true;
        script.onload = () => {
            const api = (window as unknown as { turnstile?: TurnstileApi }).turnstile;
            if (api) {
                resolve(api);
            } else {
                loading = undefined;
                reject(new Error('Turnstile loaded without defining turnstile'));
            }
        };
        script.onerror = () => {
            loading = undefined;
            script.remove();
            reject(new Error('Turnstile failed to load'));
        };
        document.head.appendChild(script);
    });
    return loading;
}

export const turnstileAvailable = () => !!TURNSTILE_SITE_KEY;


export function Turnstile({onToken, onError}: { onToken(token: string | undefined): void, onError?(err: unknown): void }) {
    const container = useRef<HTMLDivElement>(null);
    const callbacks = useRef({ onToken, onError });
    callbacks.current = { onToken, onError };

    useEffect(() => {
        // Prerendering: the saved page keeps the widget's empty place, and the browser shows it for real.
        if (!TURNSTILE_SITE_KEY || !container.current || prerendering()) {
            return;
        }
        let widgetId: string | undefined;
        let current = true;
        loadTurnstile().then(api => {
            if (!current || !container.current) {
                return;
            }
            widgetId = api.render(container.current, {
                sitekey: TURNSTILE_SITE_KEY,
                theme: document.documentElement.getAttribute('data-bs-theme') === 'dark' ? 'dark' : 'light',
                callback: token => callbacks.current.onToken(token),
                'expired-callback': () => callbacks.current.onToken(undefined),
                'error-callback': () => {
                    callbacks.current.onToken(undefined);
                    callbacks.current.onError?.(new Error('Turnstile check failed'));
                }
            });
        }, err => callbacks.current.onError?.(err));
        return () => {
            current = false;
            if (widgetId) {
                (window as unknown as { turnstile?: TurnstileApi }).turnstile?.remove(widgetId);
            }
        };
    }, []);

    return <div ref={container} className="turnstile-widget"/>;
}
